import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, BadgePercent, Mail, MessageCircle, Phone, Plus, Send, Sparkles, X } from "lucide-react";
import axios from "axios";
import Lenis from "lenis";
import { Logo } from "./Home";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP = "918667358581";
const PHONE_PRIMARY = "+91 86673 58581";
const EMAIL = "kubeilaexim@gmail.com";
const imgUrl = (u) => !u ? "" : (u.startsWith("/api") ? `${BACKEND_URL}${u}` : u);

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [idx, setIdx] = useState(0);
  const [modal, setModal] = useState(false);
  const [sent, setSent] = useState("");

  useEffect(() => {
    axios.get(`${API}/products/${id}`)
      .then(r => setP(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let frame;
    const raf = (t) => { lenis.raf(t); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); lenis.destroy(); };
  }, []);

  useEffect(() => { if (p) document.title = `${p.name} — Kubeila`; }, [p]);

  const submitQuote = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    try {
      const r = await axios.post(`${API}/quotes`, {
        name: data.get("name"), company: data.get("company"), email: data.get("email"),
        phone: data.get("phone"), message: data.get("message"), products: [p.name],
      });
      setSent(`Reference ${r.data.reference} — we will be in touch.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSent(Array.isArray(detail) ? detail.map(d => d.msg).join(" ") : (typeof detail === "string" ? detail : "Please check your details and try again."));
    }
  };

  if (notFound) return <div className="site-shell detail-shell"><nav className="nav"><a href="/"><Logo /></a></nav><div className="detail-missing" data-testid="product-not-found"><h1>Product not found</h1><a className="text-link" href="/"><ArrowLeft size={15}/> Back to catalogue</a></div></div>;
  if (!p) return <div className="site-shell detail-shell"><nav className="nav"><a href="/"><Logo /></a></nav><div className="detail-missing" data-testid="product-loading"><p className="eyebrow">LOADING…</p></div></div>;

  const imgs = (p.images && p.images.length ? p.images : (p.image ? [p.image] : [])).map(imgUrl);
  const main = imgs[idx] || "";

  return (
    <div className="site-shell detail-shell">
      <nav className="nav">
        <a href="/" aria-label="Kubeila home"><Logo /></a>
        <div className="nav-links"><a href="/#catalog" data-testid="detail-catalog-link">Catalogue</a><button className="nav-cta" onClick={() => setModal(true)} data-testid="detail-quote-button">Request quote <ArrowUpRight size={15}/></button></div>
      </nav>

      <main className="detail-main">
        <button className="text-link back-link" onClick={() => navigate("/")} data-testid="detail-back-link"><ArrowLeft size={15}/> Back to catalogue</button>
        <div className="detail-grid">
          <div className="detail-photos">
            <motion.div className="detail-main-photo" key={main} initial={{opacity:0, scale:1.03}} animate={{opacity:1, scale:1}} transition={{duration:.5}}>
              {main ? <img src={main} alt={p.name} data-testid="detail-main-image"/> : <div className="photo-empty"/>}
              <span className="detail-cat">{p.category}</span>
              {p.offer && <span className="offer-badge" data-testid="detail-offer"><BadgePercent size={12}/> {p.offer}</span>}
            </motion.div>
            {imgs.length > 1 && (
              <div className="detail-thumbs" data-testid="detail-thumbs">
                {imgs.map((u, i) => <button key={u} className={i === idx ? "active" : ""} onClick={() => setIdx(i)} data-testid={`detail-thumb-${i}`}><img src={u} alt={`${p.name} view ${i + 1}`}/></button>)}
              </div>
            )}
          </div>

          <div className="detail-info">
            <p className="eyebrow"><span>KX / {p.category}</span><span>{p.grade || "PRECISION"}</span></p>
            <h1 data-testid="detail-name">{p.name}</h1>
            <p className="detail-sub">{p.grade}{p.grade && p.coating ? " · " : ""}{p.coating}</p>
            <b className="detail-price" data-testid="detail-price">{p.price}</b>

            <div className="detail-specs">
              <div><span>DIMENSIONS</span><strong data-testid="detail-dims">{p.dims || "—"}</strong></div>
              <div><span>APPLICATION</span><strong data-testid="detail-app">{p.app || "—"}</strong></div>
              <div><span>STOCK</span><strong data-testid="detail-stock">{p.stock || "Available"}</strong></div>
            </div>

            {p.sizes && p.sizes.length > 0 && (
              <div className="detail-sizes" data-testid="detail-sizes">
                <span>AVAILABLE SIZES</span>
                <div className="size-chips">{p.sizes.map(s => <i key={s}>{s}</i>)}</div>
              </div>
            )}

            <div className="detail-actions">
              <button className="button button-primary" onClick={() => setModal(true)} data-testid="detail-add-quote"><Plus size={16}/> Request a quote</button>
              <a className="text-link" href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hello Kubeila, I'd like a quote for ${p.name}.`)}`} target="_blank" rel="noreferrer" data-testid="detail-whatsapp"><MessageCircle size={15}/> WhatsApp</a>
            </div>

            <div className="detail-contact">
              <a href={`mailto:${EMAIL}?subject=${encodeURIComponent("Quote enquiry: " + p.name)}`} data-testid="detail-email"><Mail size={14}/> {EMAIL}</a>
              <a href={`tel:${PHONE_PRIMARY.replace(/\s/g, "")}`} data-testid="detail-phone"><Phone size={14}/> {PHONE_PRIMARY}</a>
            </div>
          </div>
        </div>
      </main>

      <footer><Logo compact/><span>© 2025 KUBEILA MATERIALS EXIM</span><a href="/#catalog" data-testid="detail-footer-catalog">CATALOGUE ↑</a></footer>

      <AnimatePresence>
        {modal && (
          <motion.div className="modal-backdrop" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <motion.div className="quote-modal" initial={{y:40, opacity:0}} animate={{y:0, opacity:1}} exit={{y:40, opacity:0}}>
              <button className="close-modal" onClick={() => {setModal(false);setSent("")}} aria-label="Close quote form" data-testid="detail-close-quote"><X/></button>
              <p className="eyebrow">KX / QUOTE DESK</p>
              <h2>Quote for<br/><em>{p.name}</em></h2>
              {sent ? (
                <div className="success-message" data-testid="detail-quote-success"><Sparkles/><p>{sent}</p><button className="text-link" onClick={() => {setModal(false);setSent("")}}>Close</button></div>
              ) : (
                <form onSubmit={submitQuote} data-testid="detail-quote-form">
                  <div className="selected-products"><span>{p.name}</span></div>
                  <div className="form-grid">
                    <input name="name" placeholder="Your name *" required data-testid="detail-quote-name"/>
                    <input name="company" placeholder="Company" data-testid="detail-quote-company"/>
                    <input name="email" type="email" placeholder="Work email *" required data-testid="detail-quote-email"/>
                    <input name="phone" placeholder="Phone / WhatsApp" data-testid="detail-quote-phone"/>
                  </div>
                  <textarea name="message" placeholder="Quantity, material, sizes or any details..." data-testid="detail-quote-message"/>
                  <button className="button button-primary" type="submit" data-testid="detail-submit-quote">Send request <Send size={16}/></button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
